import { getHeaders, makeRequest, fetch } from "$utils/utils";
import { Activity } from "$types/Activity";
import config from "./config";
import { CompanyType } from "$types/CompanyType";
import constants from "$utils/constants";

const url = config.api_url;

export enum ERRORS {
  CONTACT_EXISTS = "Ce contact existe déjà, veuillez modifier les informations ou annuler la création",
  COMPANY_EXISTS = "Cette entreprise existe déjà, veuillez modifier les informations ou annuler la création",
  ADD_ERROR = "Une erreur s'est produite lors de l'ajout de l'entreprise",
  UPDATE_ERROR = "Une erreur s'est produite lors de l'édition de l'entreprise",
  MISSING_FIELD = "Donnée manquante !",
  GET_ERROR = "Impossible de récupérer les données via le siret renseigné",
}

export interface ICompany {
  _id?: string;
  agencyOf?: string;
  name: string;
  siret: string;
  sector: string;
  adress: string;
  complementAdress: string;
  postalCode: string;
  city: string;
  country: string;
  createdAt?: string;
  isActive?: boolean;
  users: any[];
  categories: any[];
  products: any[];
  companyTypes: CompanyType[];
  contributorOf?: any;
  activities?: Activity[];
  email?: string;
  notes?: string;
  technicalNotes?: string;
  medias: any[];
  supplierOf?: string[];
  wasClientOnce?: boolean;
  wasContributorOnce?: boolean;
  wasSupplierOnce?: boolean;
}

export interface DataTable {
  value: string;
  type?: string;
  isDisable?: boolean;
  link?: string;
}

// Get all active users from a company
export const gethUsersByCompany = async (companyId: string) => {
  try {
    const { data } = await fetch(`/api/user/${companyId}`);
    return data;
  } catch (err) {
    return { error: err?.message || null };
  }
};

export const FetchCompanies = async (token: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when fetching companies");
    }
    const response = res.data.data;
    return response;
  } catch (err) {
    return null;
  }
};

export const FetchClientCompanies = async (
  token: string,
  isFormatData?: boolean
) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies?companyTypes=${CompanyType.CLIENT}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when fetching companies");
    }
    const response = res.data.data;
    if (isFormatData) {
      // refactor data for Table components
      const refactorData: Array<{
        text: string;
        value: string;
        disabled?: boolean;
      }> = [];
      res.data.data.forEach((contributor: { name: any; _id: any }) => {
        refactorData.push({
          value: contributor?._id,
          text: contributor?.name,
        });
      });
      return refactorData;
    }
    return response;
  } catch (err) {
    return null;
  }
};

export const FetchCompaniesContributorsFromCompany = async (
  companyId: string,
  token: string,
  isFormatData?: boolean
) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies?contributorOf=${companyId}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res?.data?.data?.length) {
      throw new Error("Error when fetching companies");
    }

    const response = res.data.data;
    if (isFormatData) {
      // refactor data for Table components
      const refactorData: Array<{
        text: string;
        value: string;
        disabled?: boolean;
      }> = [];
      res.data.data.forEach((contributor: { name: any; _id: any }) => {
        refactorData.push({
          value: contributor?._id,
          text: contributor?.name,
        });
      });
      return refactorData;
    }
    return response;
  } catch (err) {
    return null;
  }
};

export const FetchCompany = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies/${id}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when fetching company");
    }
    const response = res.data.data;
    return response;
  } catch (err) {
    return null;
  }
};

export const FetchUsersCompany = async (token: string, idCompany: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/user`,
      "GET",
      { company: idCompany },
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when fetching company");
    }
    const response = res.data.data;
    return response;
  } catch (err) {
    return null;
  }
};

export const FetchSectors = async (token: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/sectors`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when fetching companies");
    }
    // refactor data for Table components
    const refactorData: Array<{
      label: string;
      value: string;
      disabled?: boolean;
    }> = [];
    res.data.data.forEach((sector: { code: any; label: any }) => {
      const valueData = `${sector.code} - ${sector.label}`;
      refactorData.push({
        value: valueData,
        label: valueData,
      });
    });
    return refactorData;
  } catch (err) {
    return null;
  }
};

export const AddCompany = async (token: string, data: any) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies/create`,
      "POST",
      data,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (typeof error === "string") {
      if (error.includes("company already exists")) {
        error = ERRORS.COMPANY_EXISTS;
      } else if (error.includes("user already exists")) {
        error = ERRORS.CONTACT_EXISTS;
      }
    } else {
      error = (err as any)?.response?.data?.message || ERRORS.ADD_ERROR;
    }
    throw new Error(error);
  }
};

export const EditCompany = async (token: string, data: any, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/companies/${id}`,
      "PUT",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing user");
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (typeof error === "string") {
      if (error.includes("company already exists")) {
        error = ERRORS.COMPANY_EXISTS;
      } else if (error.includes("user already exists")) {
        error = ERRORS.CONTACT_EXISTS;
      }
    } else {
      error = (err as any)?.response?.data?.message || ERRORS.UPDATE_ERROR;
    }
    throw new Error(error);
  }
};

// siret info
export const GetCompanyNameData = async (companyName: string) => {
  try {
    // To change the number of results retrieved, add nombre=XXX to the URL
    //
    // Link to the API : https://api.insee.fr/catalogue/site/themes/wso2/subthemes/insee/pages/item-info.jag?name=Sirene&version=V3.11&provider=insee
    const res = await makeRequest(
      `${constants.INSEE_API.SIRENE_ENDPOINT}?q=denominationUniteLegale:${companyName}`,
      "GET",
      undefined,
      {
        ...getHeaders(),
        "X-INSEE-Api-Key-Integration": process.env.REACT_APP_SIRET_ACCESS_TOKEN,
      }
    );
    if (!res || !res.data) {
      throw new Error("Error when getting company data with name");
    }
    return res.data;
  } catch (err) {
    return { error: ERRORS.GET_ERROR };
  }
};

export const GetSiretData = async (siret: string) => {
  try {
    const res = await makeRequest(
      `${constants.INSEE_API.SIRENE_ENDPOINT}/${siret}`,
      "GET",
      undefined,
      {
        ...getHeaders(),
        "X-INSEE-Api-Key-Integration": process.env.REACT_APP_SIRET_ACCESS_TOKEN,
      }
    );
    if (!res || !res.data) {
      throw new Error("Error when getting company data with siret");
    }
    return res.data;
  } catch (err) {
    return { error: ERRORS.GET_ERROR };
  }
};

// Link to the API to get more info on response : https://www.sirene.fr/static-resources/htm/siret_unitaire_variables_reponse.html

export type InseeApiCompany = {
  siren: string;
  nic: string;
  siret: string;
  statutDiffusionEtablissement: string;
  dateCreationEtablissement: string | Date;
  trancheEffectifsEtablissement: string;
  anneeEffectifsEtablissement: string;
  activitePrincipaleRegistreMetiersEtablissement: string;
  dateDernierTraitementEtablissement: string | Date;
  etablissementSiege: boolean;
  nombrePeriodesEtablissement: number;
  uniteLegale: {
    etatAdministratifUniteLegale: string;
    statutDiffusionUniteLegale: string;
    dateCreationUniteLegale: string | Date;
    categorieJuridiqueUniteLegale: string;
    denominationUniteLegale: string;
    sigleUniteLegale: string;
    denominationUsuelle1UniteLegale: string;
    denominationUsuelle2UniteLegale: string;
    denominationUsuelle3UniteLegale: string;
    sexeUniteLegale: string;
    nomUniteLegale: string;
    nomUsageUniteLegale: string;
    prenom1UniteLegale: string;
    prenom2UniteLegale: string;
    prenom3UniteLegale: string;
    prenom4UniteLegale: string;
    prenomUsuelUniteLegale: string;
    pseudonymeUniteLegale: string;
    activitePrincipaleUniteLegale: string;
    nomenclatureActivitePrincipaleUniteLegale: string;
    identifiantAssociationUniteLegale: string;
    economieSocialeSolidaireUniteLegale: string;
    societeMissionUniteLegale: string;
    caractereEmployeurUniteLegale: string;
    trancheEffectifsUniteLegale: string;
    anneeEffectifsUniteLegale: string;
    nicSiegeUniteLegale: string;
    dateDernierTraitementUniteLegale: string | Date;
    categorieEntreprise: string;
    anneeCategorieEntreprise: string;
  };
  adresseEtablissement: {
    complementAdresseEtablissement: string;
    numeroVoieEtablissement: string;
    indiceRepetitionEtablissement: string;
    typeVoieEtablissement: string;
    libelleVoieEtablissement: string;
    codePostalEtablissement: string;
    libelleCommuneEtablissement: string;
    libelleCommuneEtrangerEtablissement: string;
    distributionSpecialeEtablissement: string;
    codeCommuneEtablissement: string;
    codeCedexEtablissement: string;
    libelleCedexEtablissement: string;
    codePaysEtrangerEtablissement: string;
    libellePaysEtrangerEtablissement: string;
  };
  periodesEtablissement: [
    {
      dateFin: string | Date;
      dateDebut: string | Date;
      etatAdministratifEtablissement: string;
      changementEtatAdministratifEtablissement: boolean;
      enseigne1Etablissement: string;
      enseigne2Etablissement: string;
      enseigne3Etablissement: string;
      changementEnseigneEtablissement: boolean;
      denominationUsuelleEtablissement: string;
      changementDenominationUsuelleEtablissement: boolean;
      activitePrincipaleEtablissement: string;
      nomenclatureActivitePrincipaleEtablissement: string;
      changementActivitePrincipaleEtablissement: boolean;
      caractereEmployeurEtablissement: string;
      changementCaractereEmployeurEtablissement: boolean;
    }
  ];
};

export type InseeApiResponse = {
  headers: {
    statut: number;
    message: string;
    total: number;
    debut: number;
    nombre: number;
  };
  etablissements: InseeApiCompany[];
};
